import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom"
import { JcAppRoutingConfig } from "./JcAppRoutingConfig"
import * as React from "react"
import { JcAppRoute } from "./JcAppRoute"
import { JcLoadingPlaceholder } from "../components/JcLoadingPlaceholder"
import { JcScrollRestoration } from "./JcScrollRestoration"
import { JcAsyncErrorBoundary } from "../components/error/JcAsyncErrorBoundary"

export interface JcRouterProps<RoutesT extends Record<string, JcAppRoute<any, any>>> {
    readonly config: JcAppRoutingConfig<RoutesT>
}

export const JcRouter = <RoutesT extends Record<string, JcAppRoute<any, any>>>(
    props: JcRouterProps<RoutesT>
): React.ReactElement => {
    const { config } = props;
    const routeEntries = Object.entries(config.routes);

    return <BrowserRouter>
        <JcScrollRestoration />
        <Routes>
            <Route path="/"
                element={
                    <React.Suspense fallback={<JcLoadingPlaceholder id="jc-router-fallback" />}>
                        <Outlet />
                    </React.Suspense>
                }>
                {routeEntries.map(([routeKey, route]) => {
                    const RouteComponent = () => {
                        const location = useLocation();
                        const fullPath = location.pathname + location.search;
                        // Path-derived props plus opaque history state, delivered to
                        // the page as a typed `state` prop (undefined on deep-link).
                        const routeProps = { ...route.propsFromPath(fullPath), state: location.state };
                        // Per-route boundary keyed on the route, not the path: a crashed route
                        // is isolated, navigating to another route remounts a fresh boundary,
                        // but param-only navigation within a route (e.g. tabs) re-renders in
                        // place instead of remounting. resetKey still clears a caught error on
                        // any navigation, so errors recover with no manual reset.
                        return (
                            <JcAsyncErrorBoundary key={routeKey} resetKey={location.pathname}>
                                {React.createElement(route.component, routeProps)}
                            </JcAsyncErrorBoundary>
                        );
                    };

                    return <Route key={routeKey} path={route.template} element={<RouteComponent />} />
                })}
                <Route path="*" element={<config.notFoundPage />} />
            </Route>
        </Routes>
    </BrowserRouter>
}
